<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" style="display: inline;" data-cy="comment-actions-----">
        <slot></slot>
      </div>
    </template>

    <v-card data-cy="dialog-deleteComment-modal">
      <v-card-title class="d-flex justify-space-between">
        <span>
          {{ $t('routes.pod.menuComment.inviteReviewers') }}
        </span>

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-form ref="form" data-cy="comment-inviteReviewer-form">
          <div class="flex-grow-1">
            <v-select
              v-model="selectedReviewers"
              item-title="nameAndEmail"
              item-value="id"
              :items="users"
              :menu-props="{ maxHeight: '400' }"
              :label="$t('routes.pod.menuComment.selectUsers')"
              multiple
              variant="solo-filled"
              flat
              persistent-hint
            />
          </div>

          <div class="flex-grow-1">
            <v-textarea
              v-model="note"
              :label="$t('routes.pod.menuComment.reviewNote')"
              counter="2048"
              rows="3"
              required
              variant="solo-filled"
              flat
              persistent-counter
              data-cy="commentsReviewNote-text"
            />
          </div>
        </v-form>

        <div v-if="reviewers.length > 0" class="mt-5">
          <div class="text-h5">{{ $t('routes.pod.menuComment.invitedReviewers') }}</div>
          <div class="mt-4">
            <v-table density="compact">
              <template v-slot:default>
                <tbody>
                <tr v-for="reviewer in reviewers"
                    :key="reviewer.id"
                >
                  <td>
                    <span class="text-sm-body-2 d-block">{{ reviewer.name }}</span>
                    <span class="text-sm-body-5 d-block font-italic">{{ reviewer.email }}</span>
                  </td>
                </tr>
                </tbody>
              </template>
            </v-table>
          </div>
        </div>

        <div v-if="errorMessage" class="bg-error pa-2 mt-2 rounded-lg" data-cy="form-inviteReviewers-error">
          <v-icon size="small">mdi-alert-circle-outline</v-icon>
          {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="bg-success pa-2 mt-2"
             data-cy="form-inviteReviewers-success">
          <v-icon size="small">mdi-check-circle-outline</v-icon>
          {{ successMessage }}
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn
          color="primary"
          variant="flat"
          v-on:click="submitForm"
          elevation=0
          v-bind:disabled="submitDisabled"
          data-cy="podMenu-comments-inviteReviewers"
        >
          {{ $t('routes.pod.menuComment.inviteReviewers') }}
        </v-btn>

        <v-btn variant="text" @click="dialog = false" data-cy="dialog---false">
          {{ $t('common.cancel') }}
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { usePodReviewerStore } from '@/stores/PodReviewerStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [DialogActionKeys],
  name: "InviteReviewerDialog",
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      note: '',
      errorMessage: '',
      successMessage: '',
      submitDisabled: false,
      selectedReviewers: [],
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.refreshReviewers();
        this.resetForm();
      }
    }
  },
  methods: {
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.note = "";
      this.errorMessage = '';
      this.successMessage = '';
      this.submitDisabled = false;
      this.selectedReviewers = [];
    },
    refreshReviewers() {
      if (this.$canListPodService()) {
        const podReviewerStore = usePodReviewerStore();

        podReviewerStore.refreshUsersWithAccessToPod(this.pod.id);
        podReviewerStore.refreshReviewers(this.pod.id);
      }
    },
    submitForm: function() {
      this.errorMessage = '';
      if (this.selectedReviewers.length === 0) {
        this.errorMessage = 'Please, select users.';
        return false;
      }

      this.submitDisabled = true;

      const data = {
        podId: this.pod.id,
        payload: {
          reviewers: this.selectedReviewers,
          note: this.note,
        }
      };

      const podReviewerStore = usePodReviewerStore();
      podReviewerStore.inviteReviewers(data)
          .then(function (response) {
            this.note = '';
            this.selectedReviewers = [];

            podReviewerStore.refreshReviewers(this.pod.id);
            podReviewerStore.refreshUsersWithAccessToPod(this.pod.id);

            if (response && response && response.message) {
              this.successMessage = this.$replaceT(response.message.code, response.message.params, response.message.content)
            }

            this.submitDisabled = false;
          }.bind(this))

          .catch(function (error) {
            console.log(error);

            if (error && error.response && error.response.data && error.response.data.error) {
              this.errorMessage = error.response.data.error.message.content || error.response.data.error.message;
            } else {
              this.errorMessage = 'Error!';
              console.log(error);   // unhandled error
            }

            this.submitDisabled = false;
          }.bind(this));
    },
  },
  computed: {
    pod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    users: function() {
      const podReviewerStore = usePodReviewerStore();
      const results = podReviewerStore.getUsersWithAccessToPod;

      const reworkedResults = [];
      for (const result of results) {
        reworkedResults.push({ ...result, nameAndEmail: `${result.name} ( ${result.email} )` });
      }

      return reworkedResults;
    },
    reviewers: function() {
      const podReviewerStore = usePodReviewerStore();
      return podReviewerStore.getReviewers;
    },
    dialog: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },
}
</script>

<style>
</style>
